import { AfterViewInit, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom, of, Subscription } from 'rxjs';
import { ViewPort } from 'noodle/engine/context/ViewPort';
import { DOMContextBuilder } from 'noodle/engine/context/DOMContextBuilder';
import { AttachedCamera, Camera, FreeViewCamera } from 'noodle/engine/camera';
import { Engine } from 'noodle/engine/Engine';
import { InputHandler, MouseObjectInteraction } from 'noodle/engine/input';
import { Scene } from 'noodle/engine/scene/Scene';
import { SceneLoader } from 'noodle/engine/scene/SceneLoader';
import { SceneLoadProgressEvent } from 'noodle/engine/scene/SceneLoadProgress';
import { PlayerController, PointerLockProvider } from 'noodle/engine/player';
import { LoadingOverlay } from './loading-overlay/loading-overlay';
import { GraphicsSettingsPanel } from './graphics-settings/graphics-settings';
import { PerfOverlay } from './perf-overlay/perf-overlay';
import { CameraControls, createCameraActions, createPlayerActions } from './GameActions';
import { StressTestLights } from './StressTestLights';
import { DeferredSceneRenderer } from 'noodle/engine/renderer/DeferredSceneRenderer';
import { CompositeSceneRenderer } from 'noodle/engine/renderer/CompositeSceneRenderer';
import { RenderObject } from 'noodle/engine/object/RenderObject';
import { DayNightCycleFeature } from 'noodle/engine/scene/feature';
import { PhysicsFeature, PhysicsBody } from 'noodle/engine/physics';
import { GltfParser, resolveMaterialTextures, readModelTransform } from 'noodle/engine/gltf';
import { GeometryMesh, GltfMesh } from 'noodle/engine/mesh';
import { GltfAnimation } from 'noodle/engine/animation';
import { GltfAnimationDataTexture } from 'noodle/engine/texture';
import { createAnimationState } from 'noodle/engine/animation/AnimationStateFactory';
import { ParsedModel } from 'noodle/engine/scene/SceneManifestLoader';
import { createCube, createSphere } from 'noodle/engine/geometry';
import { vec3, vec4 } from 'gl-matrix';
import { TileParseWorkerClient, WorkerLike } from 'noodle/engine/workers/TileParseWorkerClient';

type EngineSettings = {
  keymap: Record<string, string[][]>;
};

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, DecimalPipe, LoadingOverlay, GraphicsSettingsPanel, PerfOverlay],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App implements AfterViewInit {
  private http = inject(HttpClient);

  @ViewChild('canvas', { static: true })
  canvas!: ElementRef<HTMLCanvasElement>;

  @ViewChild('textLayer', { static: true })
  textLayer!: ElementRef<HTMLDivElement>;

  cameras: Camera[] = [];
  scene!: Scene;
  devPanelCollapsed = false;
  graphicsDialogOpen = false;
  engine!: Engine;
  activeManifest = '';
  sceneList: { id: string; name: string; path: string }[] = [];

  loadingProgress: SceneLoadProgressEvent | null = null;

  stressTestLights?: StressTestLights;
  cameraControls?: CameraControls;
  private boxMesh?: GeometryMesh;
  physics?: PhysicsFeature;
  private playerObject?: RenderObject;
  private playerSetupDone = false;
  debugBoneImpact = false;
  private inputHandler!: InputHandler;
  private loading = false;
  private sceneLoader!: SceneLoader;
  private progressSub?: Subscription;
  private tileParseWorker?: TileParseWorkerClient;
  private terrainGenWorker?: Worker;

  playerController!: PlayerController;

  get deferredRenderer(): DeferredSceneRenderer | undefined {
    const renderer = this.engine?.renderer;
    if (renderer instanceof CompositeSceneRenderer) {
      return renderer.deferredRenderer;
    }
    return undefined;
  }

  get dayNightCycle(): DayNightCycleFeature | undefined {
    return this.scene?.features.get('day-night-cycle') as DayNightCycleFeature | undefined;
  }

  setCycleSpeed(value: number) {
    const cycle = this.dayNightCycle;
    if (cycle) cycle.speed = Number(value);
  }

  setTimeOfDay(value: number) {
    const cycle = this.dayNightCycle;
    if (cycle) cycle.timeOfDay = Number(value);
  }

  toggleCycle(enabled: boolean) {
    const cycle = this.dayNightCycle;
    if (cycle) cycle.enabled = enabled;
  }

  setCameraSpeed(value: number) {
    if (this.cameraControls) this.cameraControls.moveSpeed = Number(value);
  }

  setStressLightCount(value: number) {
    this.stressTestLights?.setCount(Number(value));
  }

  spawnBox() {
    if (!this.boxMesh || !this.physics) return;

    const obj = this.scene.objectFactory.instantiateMesh(this.boxMesh, {
      position: [(Math.random() - 0.5) * 60, 20 + Math.random() * 20, (Math.random() - 0.5) * 30],
      color: vec4.fromValues(
        0.3 + Math.random() * 0.7,
        0.3 + Math.random() * 0.7,
        0.3 + Math.random() * 0.7,
        1,
      ),
    });

    this.physics.addBody(
      new PhysicsBody(obj.trs.position, { mode: 'gravity-only', surfaceOffset: 0.5 }),
    );
  }

  launchProjectile() {
    if (!this.physics) return;
    const obj = this.scene.objectFactory.instantiateModel('7f21b482-4fd7-5a18-9648-23d70916c4ec');
    if (!obj) return;

    const cam = this.engine.activeCamera;
    const camPos = cam.cameraPosition;
    const targetPos = cam.targetPosition;

    const forward = vec3.subtract(
      vec3.create(),
      vec3.fromValues(targetPos[0] as number, targetPos[1] as number, targetPos[2] as number),
      vec3.fromValues(camPos[0] as number, camPos[1] as number, camPos[2] as number),
    );
    vec3.normalize(forward, forward);

    const speed = 20 + Math.random() * 20;
    const velocity = vec3.scale(vec3.create(), forward, speed);
    velocity[1] += 5 + Math.random() * 15;

    const angularVelocity = vec3.fromValues(
      (Math.random() - 0.5) * 10,
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 10,
    );

    this.physics.createProjectile({
      position: vec3.fromValues(camPos[0] as number, camPos[1] as number, camPos[2] as number),
      velocity,
      restitution: 0.15,
      mass: 5,
      maxBounces: 3,
      maxLifetime: 30,
      renderObject: obj,
      enableRotation: true,
      initialAngularVelocity: angularVelocity,
      angularDamping: 0.95,
      persist: true,
    });
  }

  launchArrow() {
    if (!this.physics) return;
    const obj = this.scene.objectFactory.instantiateModel('52384dbb-ddb8-549e-9671-09418efbb7f5');
    if (!obj) return;

    const cam = this.engine.activeCamera;
    const camPos = cam.cameraPosition;
    const targetPos = cam.targetPosition;

    const forward = vec3.subtract(
      vec3.create(),
      vec3.fromValues(targetPos[0] as number, targetPos[1] as number, targetPos[2] as number),
      vec3.fromValues(camPos[0] as number, camPos[1] as number, camPos[2] as number),
    );
    vec3.normalize(forward, forward);

    const speed = 40 + Math.random() * 20;
    const velocity = vec3.scale(vec3.create(), forward, speed);
    velocity[1] += 3 + Math.random() * 5;

    this.physics.createProjectile({
      position: vec3.fromValues(camPos[0] as number, camPos[1] as number, camPos[2] as number),
      velocity,
      restitution: 0,
      mass: 1,
      maxBounces: 0,
      maxLifetime: 60,
      renderObject: obj,
      enableRotation: false,
      persist: true,
      alignToVelocity: true,
      stickOnImpact: true,
      penetrationFraction: 0.2,
    });
  }

  async switchScene(manifestUrl: string) {
    if (this.loading || manifestUrl === this.activeManifest) return;
    this.activeManifest = manifestUrl;
    await this.loadScene(manifestUrl);
  }

  async ngAfterViewInit(): Promise<void> {
    // Scene list lives next to the published scene manifests on the asset
    // server so the dropdown reflects whatever has actually been published.
    // When PLAYFUL_ENV.assetBaseUrl is empty (local dev), the URL collapses to
    // a same-origin `scenes.json` served from playful's `public/`.
    //
    // Awaited before engine setup so `loadScene(this.activeManifest)` below
    // never fires with an empty manifest path — that would resolve to the
    // bare asset base URL and 404 with no CORS headers.
    const assetBaseUrl = (window as any).PLAYFUL_ENV?.assetBaseUrl ?? '';
    const fallbackList: { id: string; name: string; path: string }[] = [];
    const list = await firstValueFrom(
      this.http
        .get<{ id: string; name: string; path: string }[]>(`${assetBaseUrl}scenes.json`)
        .pipe(catchError(() => of(fallbackList))),
    );
    this.sceneList = list;
    if (list.length > 0 && !this.activeManifest) {
      this.activeManifest = list[0].path;
    }

    const settings = await firstValueFrom(this.http.get<EngineSettings>('settings.json'));

    {
      let viewPort = new ViewPort(window.innerWidth, window.innerHeight);

      let contextBuilder = new DOMContextBuilder(
        viewPort,
        this.canvas.nativeElement,
        this.textLayer.nativeElement,
        assetBaseUrl,
      );

      let primaryCamera = new FreeViewCamera(viewPort);
      primaryCamera.name = 'Free View';
      primaryCamera.fov = 70;
      primaryCamera.far = 5000;

      let playerCamera = new AttachedCamera(viewPort);
      playerCamera.name = 'player';
      playerCamera.distance = 4;
      playerCamera.yOffset = 1.6;
      playerCamera.fov = 70;
      playerCamera.far = 5000;

      this.cameras.push(primaryCamera);

      let engine = new Engine(contextBuilder, primaryCamera);
      this.engine = engine;
      this.sceneLoader = new SceneLoader(engine);

      // Create tile-parse and terrain-gen workers for off-thread tile processing.
      // Both workers are stateless across scene loads (the terrain worker receives
      // a per-scene init message from TileMeshClient.useWorker() each load).
      const tileParseRaw = new Worker(new URL('../tile-parse-shim.worker.ts', import.meta.url), {
        type: 'module',
      });
      this.tileParseWorker = new TileParseWorkerClient(tileParseRaw as unknown as WorkerLike);
      this.terrainGenWorker = new Worker(
        new URL('../terrain-gen-shim.worker.ts', import.meta.url),
        { type: 'module' },
      );

      // Load saved graphics settings from localStorage
      const savedGraphics = GraphicsSettingsPanel.loadSaved();
      if (savedGraphics) {
        engine.graphicsSettings = savedGraphics;
      }

      let inputHandler = new InputHandler(this.canvas.nativeElement);
      inputHandler.hookEvents();
      this.inputHandler = inputHandler;

      const cameraActionBundle = createCameraActions(engine, primaryCamera);
      const cameraActions = cameraActionBundle.actions;
      this.cameraControls = cameraActionBundle.controls;
      let playerActions = createPlayerActions(engine);
      inputHandler.setInputActions(cameraActions);
      let mouseInteraction = new MouseObjectInteraction(engine);

      inputHandler.bindKeyMapping(settings.keymap);
      inputHandler.bindMouseEventAdapter(mouseInteraction);

      engine.tickEventManager.registerFrameEvent(() => inputHandler.handleKeyboard());
      engine.tickEventManager.registerTimeEvent(() => inputHandler.handleMouse(), 50);

      // Pointer lock provider — thin DOM adapter
      const canvas = this.canvas.nativeElement;
      const pointerLock: PointerLockProvider = {
        requestLock: () => canvas.requestPointerLock(),
        exitLock: () => {
          if (document.pointerLockElement === canvas) document.exitPointerLock();
        },
        isLocked: () => document.pointerLockElement === canvas,
      };

      canvas.addEventListener('mousemove', (e: MouseEvent) => {
        if (pointerLock.isLocked()) {
          pointerLock.onMouseDelta?.(e.movementX, e.movementY);
        }
      });
      document.addEventListener('pointerlockchange', () => {
        if (!pointerLock.isLocked() && this.playerController.controlMode === 'player') {
          this.playerController.setControlMode('free-camera');
        }
      });
      canvas.addEventListener('mousedown', () => {
        if (this.playerController.controlMode === 'player' && !pointerLock.isLocked()) {
          pointerLock.requestLock();
        }
      });
      canvas.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.code === 'Tab') {
          e.preventDefault();
          this.togglePlayerMode();
        }
        if (e.code === 'KeyB') {
          this.debugBoneImpact = !this.debugBoneImpact;
        }
      });

      // Debug: click to simulate bone impact
      canvas.addEventListener('click', (e: MouseEvent) => {
        if (!this.debugBoneImpact || !this.physics) return;
        const rect = canvas.getBoundingClientRect();
        // Scale CSS pixels to render pixels for getRay
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = canvas.height - (e.clientY - rect.top) * scaleY; // flip Y for GL
        const ray = engine.activeCamera.getRay(x, y);
        const hit = this.physics.boneHitDetection.testRay(ray);
        if (hit) {
          this.physics.boneImpulseSystem.applyImpulse(
            hit.target.renderObject,
            hit.boneCapsule.jointIndex,
            ray.direction,
            0.8,
          );
        }
      });

      // Create player controller
      this.playerController = new PlayerController({
        engine,
        inputHandler,
        playerCamera,
        freeCamera: primaryCamera,
        cameraActions,
        playerActions,
        pointerLock,
        onAttack: () => {
          if (!this.physics || !this.playerObject) return;
          this.physics.meleeAttackSystem.triggerAttack(this.playerObject, {
            range: 1.6,
            angle: Math.PI / 2, // 90° cone
            power: 0.9,
            hitDelay: 0.3,
          });
        },
      });

      await this.loadScene(this.activeManifest);

      // Handle window resize with debounce to avoid thrashing FBO allocations
      let resizeTimer: ReturnType<typeof setTimeout> | undefined;
      window.addEventListener('resize', () => {
        const w = window.innerWidth;
        const h = window.innerHeight;
        const scale = this.deferredRenderer?.graphicsSettings.renderScale ?? 1.0;
        contextBuilder.resizeCanvas(w, h, scale);
        for (const cam of this.cameras) {
          (cam as any).invalidateProjection?.();
        }
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => engine.resize(), 150);
      });

      engine.start();
    }
  }

  private findFirstAnimatedObject(
    namedObjects: Map<string, RenderObject[]>,
  ): RenderObject | undefined {
    for (const [, objects] of namedObjects) {
      for (const obj of objects) {
        if (obj.isAnimated) return obj;
      }
    }
    return undefined;
  }

  private async loadMutantPlayer(
    scene: Scene,
    spawnPoint?: number[],
  ): Promise<RenderObject | null> {
    const mutantId = '443ec156-200c-585a-94c9-066dae591cab';
    if (!scene.modelRegistry.has(mutantId)) {
      try {
        const glbData = await scene.ctx.resourceLoader.loadResource(`models/${mutantId}.glb`);
        const parser = new GltfParser(glbData);
        const parsed = parser.parse();
        const resolvedTextures = parser.resolveTextures(parsed.json, parsed.binaries);
        const materials = await resolveMaterialTextures(scene.ctx, resolvedTextures);
        const meshes = parser.resolveSceneMeshes(parsed.json, parsed.binaries);
        const rawMeshes = parser.resolveAllMeshes(parsed.json, parsed.binaries);
        const model: ParsedModel = {
          parser,
          meshes,
          rawMeshes,
          meshByName: new Map(meshes.map((m) => [m.name, m])),
          rawMeshByName: new Map(rawMeshes.map((m) => [m.name, m])),
          materials,
          lightmapTextures: [],
          json: parsed.json,
          binaries: parsed.binaries,
        };
        scene.modelRegistry.set(mutantId, model);
      } catch (e) {
        console.warn('Failed to load mutant model', e);
        return null;
      }
    }

    const model = scene.modelRegistry.get(mutantId)!;
    const raw = model.rawMeshes[0];
    if (!raw || raw.primitives.length === 0) return null;

    const prim = raw.primitives[0];
    const mesh = new GltfMesh('mutant_player', scene.ctx, scene, prim);

    const anim = new GltfAnimation(model.json, model.binaries);
    anim.preComputeAnimations();
    mesh.animationDataTexture = new GltfAnimationDataTexture(scene.ctx, anim);
    mesh.animation = anim;

    const obj = scene.objectFactory.instantiateMesh(mesh);
    if (prim.materialIndex !== undefined) {
      const mat = model.materials.get(prim.materialIndex);
      if (mat) obj.material = mat;
    }

    const modelTransform = readModelTransform(model.json);
    if (modelTransform?.scale !== undefined) obj.trs.scale = modelTransform.scale;
    if (modelTransform?.rotation) {
      obj.trs.setRotation(
        modelTransform.rotation[0],
        modelTransform.rotation[1],
        modelTransform.rotation[2],
      );
    }

    if (spawnPoint) {
      obj.trs.position = spawnPoint;
    }

    obj.animationState = createAnimationState(obj, model.json);
    obj.animationState.switchState('Idle');

    const physics = scene.features.get('physics') as PhysicsFeature | undefined;
    physics?.boneHitDetection.registerAnimated(obj, model.json);

    return obj;
  }

  async togglePlayerMode(): Promise<void> {
    if (this.playerController.controlMode === 'player') {
      this.playerController.setControlMode('free-camera');
      return;
    }

    const scene = this.scene;
    if (!scene) return;

    if (!this.playerSetupDone) {
      const cam = this.engine.activeCamera;
      const pos = cam.cameraPosition;
      const cx = pos[0] as number;
      const cy = pos[1] as number;
      const cz = pos[2] as number;

      let spawnY = cy;
      if (scene.volumeGrid) {
        await scene.volumeGrid.loadInitialTiles(cx, cz);
        const floor = scene.volumeGrid.getFloorHeightBelow(cx, cy + 50, cz);
        if (floor !== null) spawnY = floor;
      }

      const obj = await this.loadMutantPlayer(scene, [cx, spawnY, cz]);
      if (obj) {
        this.playerObject = obj;
        this.playerController.setupEntity(scene, obj);
        this.playerController.registerFrameEvents(scene);
        this.playerSetupDone = true;
      }
    }

    if (this.playerObject) {
      this.playerController.setControlMode('player');
    }
  }

  private async loadScene(manifestUrl: string) {
    this.loading = true;

    this.playerController.teardown();

    // Clear app-level scene state
    this.stressTestLights = undefined;
    this.boxMesh = undefined;
    this.physics = undefined;
    this.playerObject = undefined;
    this.playerSetupDone = false;

    // Subscribe to loading progress for the overlay
    this.progressSub?.unsubscribe();
    this.progressSub = this.sceneLoader.progress$.subscribe((event) => {
      this.loadingProgress = event;
    });

    const inputHandler = this.inputHandler;

    try {
      const { scene, manifest, namedObjects } = await this.sceneLoader.loadScene(() => {
        const s = new Scene(this.engine, inputHandler);
        s.debugManager.enable('fps');
        if (this.tileParseWorker) s.tileParseWorker = this.tileParseWorker;
        if (this.terrainGenWorker)
          s.terrainGenWorker = this.terrainGenWorker as unknown as WorkerLike;
        return s;
      }, manifestUrl);

      this.scene = scene;

      // Drop the orbit center on the terrain at the world origin so the eye
      // floats above the surface instead of clipping through it. (Scene
      // manifests carry a `spawnPoint` field but it's used for the player
      // object, not the camera, and isn't authored yet — so we ignore it
      // here and just sample terrain at origin.)
      if (this.cameras.length > 0) {
        const terrainY = scene.terrainStreamer?.getHeightField()?.sampleHeight(0, 0) ?? 0;
        for (const cam of this.cameras) {
          if ('x' in cam) {
            (cam as any).x = 0;
            (cam as any).y = terrainY;
            (cam as any).z = 0;
          }
        }
      }

      // Physics setup (if physics feature is present). Bone hit detection
      // registration for animated objects is handled by SceneManifestLoader.
      this.physics = scene.features.get('physics') as PhysicsFeature | undefined;
      if (this.physics) {
        this.boxMesh = GeometryMesh.fromCollection('physics-box', scene, createCube(1));
      }

      // Drive navmesh streamer from player position only — not from the free-view camera.
      // The navmesh exists for NPC pathfinding; loading tiles around a flying camera
      // wastes CPU and causes constant tile churn.
      scene.tickEventManager.registerFrameEvent(() => {
        const player = this.playerObject;
        if (player && scene.navMeshStreamer) {
          const pos = player.position;
          scene.navMeshStreamer.updateCamera(pos[0], pos[2]);
        }
      });

      // Stress test lights
      const spread = { x: 50, yCenter: 30, y: 30, z: 25 };
      this.stressTestLights = new StressTestLights(scene, spread);
      scene.tickEventManager.registerFrameEvent(() => {
        this.stressTestLights?.update(performance.now());
      });
    } finally {
      this.progressSub?.unsubscribe();
      this.loadingProgress = null;
      this.loading = false;
    }
  }
}
